import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
} from 'reactstrap';
import { toast } from 'react-toastify';
import Api from '../../../util/api';
import { partial } from '../../../util/translation/translation';
import {
  formatMoney,
  isValidKrInput,
  krInputToOre,
  oreToKrInput,
  paymentErrorMessage,
  refundableAmount,
  refundedAmountOf,
} from '../payments';

/**
 * Refunds a Stripe payment, all of what's left or less. The booking stays:
 * removing the participant is a separate step.
 * target: { transaction, name }
 */
const RefundModal = ({ target, currency, onClose, onRefunded }) => {
  const p = partial('EventPayments');
  const s = partial('shared');
  const [amount, setAmount] = useState('');
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const transaction = target?.transaction;
  const left = refundableAmount(transaction);

  useEffect(() => {
    if (target) {
      setAmount(oreToKrInput(refundableAmount(target.transaction)));
      setError('');
    }
  }, [target]);

  const refund = async () => {
    const ore = krInputToOre(amount);
    if (!isValidKrInput(amount) || !ore || ore <= 0) {
      setError(p('errorAmount'));
      return;
    }
    if (ore > left) {
      setError(
        p('errorRefundTooLarge', { amount: formatMoney(left, currency) })
      );
      return;
    }
    setBusy(true);
    try {
      // The whole rest is refunded without an amount
      await Api.transactions.refundTransaction(
        transaction.id,
        ore === left ? undefined : ore
      );
      toast.success(
        p('refunded', {
          amount: formatMoney(ore, currency),
          name: target.name || transaction.userEmail || '',
        })
      );
      onRefunded();
    } catch (err) {
      setError(paymentErrorMessage(err));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal isOpen={!!target} toggle={onClose} centered>
      <ModalHeader toggle={onClose}>{p('refundTitle')}</ModalHeader>
      {transaction && (
        <ModalBody>
          <p className="mb-2">
            <strong>{target.name || transaction.userEmail}</strong>
          </p>
          <dl className="event-payments__facts">
            <div>
              <dt>{p('paid')}</dt>
              <dd>{formatMoney(transaction.price, currency)}</dd>
            </div>
            {refundedAmountOf(transaction) > 0 && (
              <div>
                <dt>{p('alreadyRefunded')}</dt>
                <dd>{formatMoney(refundedAmountOf(transaction), currency)}</dd>
              </div>
            )}
            <div>
              <dt>{p('leftToRefund')}</dt>
              <dd>{formatMoney(left, currency)}</dd>
            </div>
          </dl>
          <label className="form-header d-block mb-1" htmlFor="refundAmount">
            {p('refundAmount', { currency })}
          </label>
          <Input
            id="refundAmount"
            inputMode="decimal"
            value={amount}
            onChange={(e) => {
              setAmount(e.target.value);
              setError('');
            }}
          />
          <small className="event-payments__hint">
            {p('refundKeepsBooking')}
          </small>
          {error && (
            <span className="form__form-group-error d-block mt-2">{error}</span>
          )}
        </ModalBody>
      )}
      <ModalFooter>
        <Button color="secondary" className="mb-0" onClick={onClose}>
          {s('cancel')}
        </Button>
        <Button
          color="danger"
          className="mb-0"
          disabled={busy || !left}
          onClick={refund}
        >
          {p('refund')}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

RefundModal.propTypes = {
  target: PropTypes.shape({
    transaction: PropTypes.shape({ id: PropTypes.string }),
    name: PropTypes.string,
  }),
  currency: PropTypes.string,
  onClose: PropTypes.func.isRequired,
  onRefunded: PropTypes.func.isRequired,
};

RefundModal.defaultProps = {
  target: null,
  currency: '',
};

export default RefundModal;
